import React, { useMemo } from 'react';
import { ApproveExternalAgreementChangeRequestOptions } from '@/services/external-agreements';
import { ChangeRequestParticipantSelectListItem } from '@/seller/components/change-requests/ChangeRequestParticipantSelectList';
import {
    ExternalAgreement,
    SellerChangeRequestStatus,
    BuyerOfferStatus,
    ChangeRequestProgressInfoOffer,
    ChangeRequestWithChatInfo,
    BuyerOfferLine,
    ExternalAgreementLine,
    ProjectTag,
} from '@/types';
import ChangeRequestListItem from '@/seller/components/change-requests/ChangeRequestList/ChangeRequestListItem';
import BuyerChangeRequestOffer from '@/buyer/components/BuyerChangeRequests/BuyerChangeRequestOffer';
import { Trans } from '@lingui/react/macro';
import Banner from '@/components/Banner';
import { ExternalAgreementSignMode } from './ExternalAgreementApproveWizard';
import theme from './ExternalAgreementApproveWizard.module.scss';
import { useAsyncState } from '@/utils/hooks';
import { getChangeRequestAvailableParticipants } from '@/services/change-requests';
import { ExternalAgreementApproveOfferOptions } from './ExternalAgreementApproveWizardOffer';
import { uniq } from 'lodash-es';

interface ExternalAgreementApproveWizardSummaryProps {
    changeRequestOptions: ApproveExternalAgreementChangeRequestOptions;
    offerOptions: ExternalAgreementApproveOfferOptions;
    selectedParticipants: ChangeRequestParticipantSelectListItem[];
    externalAgreement: ExternalAgreement;
}

const ExternalAgreementApproveWizardSummary: React.FC<ExternalAgreementApproveWizardSummaryProps> = ({
    changeRequestOptions,
    offerOptions,
    selectedParticipants,
    externalAgreement,
}) => {
    let status = SellerChangeRequestStatus.AwaitingOffers;
    if (selectedParticipants.length === 1) {
        if (offerOptions.signMode === ExternalAgreementSignMode.PreSigned) {
            status = SellerChangeRequestStatus.AcceptedByBuyer;
        } else {
            status = SellerChangeRequestStatus.SentToBuyer;
        }
    }

    const [availableParticipants] = useAsyncState(
        () => getChangeRequestAvailableParticipants(externalAgreement.projectId),
        [externalAgreement.projectId],
        null
    );

    const participants = availableParticipants?.filter((x) => {
        if (selectedParticipants.some((selected) => selected.profileId === x.profileId && selected.userId === x.userId)) {
            return true;
        }

        return false;
    });

    const changeRequestData: ChangeRequestWithChatInfo = useMemo(
        () => ({
            residentialUnitId: externalAgreement.residentialUnitId,
            residentialUnitName: externalAgreement.residentialUnitName,
            subject: changeRequestOptions.subject,
            description: changeRequestOptions.description,
            internalDescription: changeRequestOptions.internalDescription,
            tags: uniq(externalAgreement.lines.flatMap((x) => x.tags).filter((x) => x) as ProjectTag[]),
            status: status,
            id: 1234,
            createdByUser: externalAgreement.createdByUser,
            projectId: externalAgreement.projectId,
            attachments: [],
            closeNote: '',
            closedByUser: null,
            hasOffers: false,
            hasParticipants: false,
            isClosed: false,
            isFollowing: true,
            lastMessageDate: null,
            optionGroupOverrides: [],
            privateCommentThreadId: 0,
            publicCommentThreadId: 0,
            rejectReason: null,
            reviewedByUser: null,
            submitDate: new Date(),
            unreadMessages: 0,
            waitingForCurrentUser: false,
            isArchived: false,
            externalAgreementId: null,
            lastActiveOfferId: null,
        }),
        [externalAgreement, changeRequestOptions, status]
    );

    const offerData: ChangeRequestProgressInfoOffer | null = useMemo(() => {
        function getOfferLines(participants: ExternalAgreementLine[]): BuyerOfferLine[] | null {
            if (offerOptions.showSubtotalsToBuyers) {
                if (!participants) {
                    return [];
                }

                return participants.map((x) => ({
                    id: x.id,
                    description: x.description,
                    finalPrice: x.priceCalculation!.finalPrice,
                    basePrice: null,
                    feeSetInfo: null,
                    itemCategoryId: x.itemCategoryId,
                }));
            }

            return null;
        }

        if (selectedParticipants.length !== 1) {
            return null;
        }
        return {
            id: 1234,
            note: offerOptions.offerMessage,
            groups: [
                {
                    note: '',
                    attachments: externalAgreement.files,
                    price: externalAgreement.finalPrice,
                    providedByProfile: externalAgreement.createdByProfile,
                    providedByUser: externalAgreement.createdByUser,
                    offerLines: getOfferLines(externalAgreement.lines),
                    showSubtotalsToBuyers: false,
                },
            ],
            sendDate: new Date(),
            sentByProfile: externalAgreement.createdByProfile,
            sentByUser: externalAgreement.createdByUser,
            status:
                offerOptions.signMode === ExternalAgreementSignMode.PreSigned
                    ? BuyerOfferStatus.Accepted
                    : BuyerOfferStatus.Pending,
            totalPrice: externalAgreement.finalPrice!,
        } as ChangeRequestProgressInfoOffer;
    }, [externalAgreement, offerOptions, selectedParticipants]);

    const renderParticipantsBanner = () => {
        if (!participants || participants.length < 2) {
            return;
        }

        const participantsText = participants.map((x) => x.displayName).join(', ');

        return (
            <Banner type="info">
                <Trans>You are about to create a change request with the following participants: {participantsText}.</Trans>
            </Banner>
        );
    };

    return (
        <>
            <div className={theme.smallHeader}>
                <Trans>Change request</Trans>
            </div>

            {renderParticipantsBanner()}

            <ChangeRequestListItem
                showExpanded={false}
                onChange={() => null}
                reloadChangeRequests={() => null}
                defaultExpanded={false}
                changeRequest={changeRequestData}
            />

            {selectedParticipants.length === 1 && (
                <>
                    <div className={theme.smallHeader}>
                        <Trans>Offer</Trans>
                    </div>
                    <BuyerChangeRequestOffer isPreview changeRequestId={1234} onChange={() => null} offer={offerData!} />
                </>
            )}

            {offerOptions.signMode === ExternalAgreementSignMode.PreSigned && (
                <Banner type="warning">
                    <Trans>
                        <strong>Please note</strong>, by approving this agreement you will create a signed change request and
                        orders. The buyer will not need to sign this.{' '}
                        <strong>
                            Make sure that in this offer there is at least one document that is signed by the buyer.
                        </strong>
                    </Trans>
                </Banner>
            )}
        </>
    );
};

export default ExternalAgreementApproveWizardSummary;
